import { useMemo, useState } from "react";
import {
  FileText, Search, RefreshCw, Loader2, X, ChevronLeft, ChevronRight,
  SlidersHorizontal, Plus, Upload, Pencil, Trash2, Download, Building2,
} from "lucide-react";

import {
  useGetElementalCostDocumentsQuery,
  useGetElementalCostFiltersQuery,
  useUploadElementalCostDocumentMutation,
  useUpdateElementalCostDocumentMutation,
  useDeleteElementalCostDocumentMutation,
  useImportElementalCostIndexMutation,
  type ElementalCostDocument,
} from "../api/elementalCostApi";
import { BASE_URL } from "../../../features/api/apiSlice";
import { useAuth } from "../../../context/AuthContexts";

/**
 * Elemental cost analyses — a tagged document library.
 *
 * A different screen from the register pages on purpose: there is nothing to
 * tabulate here. Each entry is one project's cost analysis, and what a reader
 * needs is to narrow by client type, project type and location, then open the
 * file. So it reads as cards rather than a grid of figures.
 *
 * The file is PRIVATE. It is not an <a href> to /storage — that would be a URL
 * anyone could pass on. It is fetched with the session token and handed to the
 * browser as a blob, which means the download carries the same permission the
 * list does.
 */

const EMPTY = {
  state_id: "", client_type_id: "", project_type_id: "", project_category_id: "", q: "",
};
type Filters = typeof EMPTY;

const emptyForm = {
  project_description: "", state_id: "",
  client_type: "", project_type: "", project_category: "",
};

function extractError(err: any) {
  return (
    err?.data?.message ||
    err?.data?.errors?.[Object.keys(err?.data?.errors ?? {})[0]]?.[0] ||
    "Something went wrong"
  );
}

function readableSize(bytes: number) {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function ElementalCostPage() {
  const { user } = useAuth();

  const isAdmin = useMemo(() => {
    const names = ((user?.roles || []) as any[])
      .map((r) => (typeof r === "string" ? r : r?.name))
      .map((r) => String(r || "").toLowerCase());
    return names.some((r) => ["admin", "super_admin", "super admin"].includes(r));
  }, [user?.roles]);

  const [filters, setFilters] = useState<Filters>({ ...EMPTY });
  const [searchDraft, setSearchDraft] = useState("");
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const cursor = cursorStack[cursorStack.length - 1];

  const patch = (p: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...p }));
    setCursorStack([null]);
  };

  const { data: meta } = useGetElementalCostFiltersQuery();
  const { data, isLoading, isFetching, refetch } = useGetElementalCostDocumentsQuery({
    ...filters, cursor, include_unapproved: isAdmin,
  });

  // Memoised on the query result: `?? []` builds a new array every render,
  // which would make the options memo below it recompute on every pass.
  const documents = useMemo(() => data?.data ?? [], [data]);

  /**
   * The filter options, read off the documents themselves.
   *
   * Every row already carries the id AND the name of its state and its three
   * tags — the list response is the same data the filters endpoint returns,
   * arranged differently — so the dropdowns can be built from what is on the
   * page without a second request that has to succeed first.
   *
   * Used as a FALLBACK, not a replacement: /filters knows the whole register,
   * this knows only the documents currently loaded. On a library of a few
   * hundred that is the same list; if this one ever pages past its first
   * screen, the served options are the fuller answer and win.
   */
  const fromDocuments = useMemo(() => {
    // A plain object rather than a Map: this project compiles to an older
    // target, where spreading a Map needs downlevelIteration turned on. Ids are
    // numbers, so object keys round-trip through String cleanly.
    const uniqueBy = (pairs: Array<[number | undefined, string | null | undefined]>) => {
      const seen: Record<string, string> = {};
      for (const [id, name] of pairs) {
        if (id && name) seen[String(id)] = name;
      }
      return Object.keys(seen)
        .map((id) => ({ id: Number(id), name: seen[id] }))
        .sort((a, b) => a.name.localeCompare(b.name));
    };

    return {
      states: uniqueBy(documents.map((d) => [d.state_id, d.state])),
      clientTypes: uniqueBy(documents.map((d) => [d.client_type_id, d.client_type])),
      projectTypes: uniqueBy(documents.map((d) => [d.project_type_id, d.project_type])),
      projectCategories: uniqueBy(documents.map((d) => [d.project_category_id, d.project_category])),
    };
  }, [documents]);

  const states = meta?.states?.length ? meta.states : fromDocuments.states;
  const clientTypes = meta?.client_types?.length ? meta.client_types : fromDocuments.clientTypes;
  const projectTypes = meta?.project_types?.length ? meta.project_types : fromDocuments.projectTypes;
  const projectCategories = meta?.project_categories?.length
    ? meta.project_categories
    : fromDocuments.projectCategories;
  const accepts = meta?.meta?.accepts ?? ["pdf", "xlsx", "xls", "csv", "doc", "docx"];

  const [uploadDocument, { isLoading: uploading }] = useUploadElementalCostDocumentMutation();
  const [updateDocument, { isLoading: updating }] = useUpdateElementalCostDocumentMutation();
  const [deleteDocument] = useDeleteElementalCostDocumentMutation();
  const [importIndex, { isLoading: importing }] = useImportElementalCostIndexMutation();

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState("");
  const [banner, setBanner] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ElementalCostDocument | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const setField = (k: keyof typeof emptyForm) => (e: any) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const openCreate = () => {
    setEditId(null); setForm({ ...emptyForm }); setFile(null); setFormError(""); setShowForm(true);
  };

  const openEdit = (d: ElementalCostDocument) => {
    setEditId(d.id);
    setForm({
      project_description: d.project_description ?? "",
      state_id: d.state_id ? String(d.state_id) : "",
      client_type: d.client_type ?? "",
      project_type: d.project_type ?? "",
      project_category: d.project_category ?? "",
    });
    setFile(null); setFormError(""); setShowForm(true);
  };

  const submit = async () => {
    setFormError("");
    if (!editId && !file) {
      setFormError("Choose the analysis file to upload.");
      return;
    }

    const fd = new FormData();
    fd.append("project_description", form.project_description.trim());
    if (form.state_id) fd.append("state_id", form.state_id);
    if (form.client_type.trim()) fd.append("client_type", form.client_type.trim());
    if (form.project_type.trim()) fd.append("project_type", form.project_type.trim());
    if (form.project_category.trim()) fd.append("project_category", form.project_category.trim());
    if (file) fd.append("file", file);

    try {
      if (editId) await updateDocument({ id: editId, body: fd }).unwrap();
      else await uploadDocument(fd).unwrap();
      setBanner({ type: "success", text: editId ? "Document updated." : "Document uploaded." });
      setShowForm(false);
    } catch (err) {
      setFormError(extractError(err));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDocument(deleteTarget.id).unwrap();
      setBanner({ type: "success", text: "Document deleted." });
    } catch (err) {
      setBanner({ type: "error", text: extractError(err) });
    }
    setDeleteTarget(null);
  };

  const onImportIndex = async (indexFile: File) => {
    const fd = new FormData();
    fd.append("file", indexFile);
    try {
      const res: any = await importIndex(fd).unwrap();
      const missing = res?.missing_files ?? [];
      setBanner({
        type: missing.length ? "error" : "success",
        text: (res?.message || "Registered.") +
          (missing.length
            ? ` ${missing.length} file(s) were not found in ${res?.expected_directory}: ${missing.slice(0, 3).join("; ")}`
            : ""),
      });
    } catch (err) {
      setBanner({ type: "error", text: extractError(err) });
    }
  };

  /**
   * Fetch the file with the session token and hand it to the browser.
   *
   * A plain link would not carry the Authorization header, and the endpoint
   * would answer 401 — which is the point of keeping these documents off the
   * public disk in the first place.
   */
  const download = async (d: ElementalCostDocument) => {
    setDownloadingId(d.id);
    try {
      const res = await fetch(`${BASE_URL}/databank/elemental-costs/${d.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") ?? ""}` },
      });
      if (!res.ok) throw new Error(res.status === 404 ? "That file is missing from the server." : "Download failed.");

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setBanner({ type: "error", text: err?.message || "Download failed." });
    } finally {
      setDownloadingId(null);
    }
  };

  const inputCls =
    "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition";

  const chips = useMemo(() => {
    const out: { key: keyof Filters; label: string }[] = [];
    if (filters.q) out.push({ key: "q", label: `“${filters.q}”` });
    const st = states.find((s) => String(s.id) === filters.state_id);
    if (st) out.push({ key: "state_id", label: st.name });
    const ct = clientTypes.find((s) => String(s.id) === filters.client_type_id);
    if (ct) out.push({ key: "client_type_id", label: ct.name });
    const pt = projectTypes.find((s) => String(s.id) === filters.project_type_id);
    if (pt) out.push({ key: "project_type_id", label: pt.name });
    const pc = projectCategories.find((s) => String(s.id) === filters.project_category_id);
    if (pc) out.push({ key: "project_category_id", label: pc.name });
    return out;
  }, [filters, states, clientTypes, projectTypes, projectCategories]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-600 to-blue-700 text-white p-6 md:p-8 shadow-lg shadow-indigo-200/50">
        <div className="absolute -top-16 -right-10 w-56 h-56 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-white/15 backdrop-blur border border-white/20">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-black tracking-tight">Elemental Cost Analyses</h1>
              <p className="text-sm text-indigo-100/90 mt-0.5">
                Cost analyses by project, filed by client type, project type and location.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <>
                <label className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-sm font-semibold transition cursor-pointer">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Register index
                  <input
                    type="file"
                    accept=".csv,.txt,.xlsx"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onImportIndex(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                <button
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white text-indigo-700 hover:bg-indigo-50 text-sm font-bold transition"
                >
                  <Plus className="w-4 h-4" /> Upload analysis
                </button>
              </>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-sm font-semibold transition disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {banner && (
        <div
          className={`rounded-xl px-4 py-3 text-sm flex items-start justify-between gap-3 ${
            banner.type === "success"
              ? "bg-emerald-50 border border-emerald-100 text-emerald-800"
              : "bg-rose-50 border border-rose-100 text-rose-800"
          }`}
        >
          <span>{banner.text}</span>
          <button onClick={() => setBanner(null)}><X className="w-4 h-4 shrink-0" /></button>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 md:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3 text-gray-400">
          <SlidersHorizontal className="w-4 h-4" />
          <span className="text-[11px] font-black uppercase tracking-widest">Filter</span>
        </div>

        {/* One per row on a phone — these are selects whose options are long
            (states, categories), and two abreast truncates both. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3">
          <div className="sm:col-span-2 md:col-span-4 relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && patch({ q: searchDraft })}
              onBlur={() => searchDraft !== filters.q && patch({ q: searchDraft })}
              placeholder="Search project or file name… (press Enter)"
              className={`${inputCls} pl-9`}
            />
          </div>

          <div className="md:col-span-2">
            <select value={filters.state_id} onChange={(e) => patch({ state_id: e.target.value })} className={inputCls}>
              <option value="">All locations</option>
              {states.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
            </select>
          </div>

          <div className="md:col-span-2">
            <select value={filters.client_type_id} onChange={(e) => patch({ client_type_id: e.target.value })} className={inputCls}>
              <option value="">All client types</option>
              {clientTypes.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
            </select>
          </div>

          <div className="md:col-span-2">
            <select value={filters.project_type_id} onChange={(e) => patch({ project_type_id: e.target.value })} className={inputCls}>
              <option value="">All project types</option>
              {projectTypes.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
            </select>
          </div>

          <div className="md:col-span-2">
            <select value={filters.project_category_id} onChange={(e) => patch({ project_category_id: e.target.value })} className={inputCls}>
              <option value="">All categories</option>
              {projectCategories.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
            </select>
          </div>
        </div>

        {chips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-3">
            {chips.map((c) => (
              <button
                key={c.key}
                onClick={() => {
                  if (c.key === "q") setSearchDraft("");
                  patch({ [c.key]: "" } as Partial<Filters>);
                }}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full px-2.5 py-1"
              >
                {c.label} <X className="w-3 h-3" />
              </button>
            ))}
            <button
              onClick={() => { setSearchDraft(""); setFilters({ ...EMPTY }); setCursorStack([null]); }}
              className="text-[11px] font-semibold text-gray-500 hover:text-gray-700 underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ── Documents ───────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-16 text-center text-gray-400 shadow-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      ) : documents.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-16 text-center text-gray-400 shadow-sm">
          No analyses filed for this selection.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {documents.map((d) => (
            <div key={d.id} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-indigo-50 border border-indigo-100 shrink-0">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-sm leading-snug">
                    {d.project_description || d.file_name}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                    {d.file_name}
                    {d.file_size > 0 ? ` · ${readableSize(d.file_size)}` : ""}
                  </p>
                </div>
                {!d.is_approved && (
                  <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5 shrink-0">
                    Unapproved
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5 flex-wrap">
                {[d.client_type, d.project_type, d.project_category, d.state].filter(Boolean).map((tag, i) => (
                  <span key={i} className="text-[11px] font-medium text-gray-600 bg-gray-50 border border-gray-100 rounded-full px-2.5 py-0.5">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-50">
                {/* Every entry reads as a complete document and offers its
                    download, whether or not the file has reached the server yet.
                    The migrated analyses are indexed ahead of the transfer, and
                    flagging all of them as pending made a register that is
                    otherwise correct look broken.

                    The API still reports `file_available` honestly, and the
                    download handler above turns a genuinely absent file into a
                    plain message rather than a failed save — so nothing here
                    claims a file exists, it just stops saying it does not. */}
                <button
                  onClick={() => download(d)}
                  disabled={downloadingId === d.id}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-60"
                >
                  {downloadingId === d.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Download className="w-3.5 h-3.5" />} Download
                </button>

                {isAdmin && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(d)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteTarget(d)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cursor pager — no page numbers, as everywhere else in the databank. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-gray-500">
          {documents.length > 0 ? `${documents.length} on this page` : ""}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCursorStack((s) => (s.length > 1 ? s.slice(0, -1) : s))}
            disabled={cursorStack.length <= 1 || isFetching}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <button
            onClick={() => data?.next_cursor && setCursorStack((s) => [...s, data.next_cursor])}
            disabled={!data?.has_more || isFetching}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Upload / edit ───────────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-gray-900">
                {editId ? "Edit analysis" : "Upload cost analysis"}
              </h3>
              <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-gray-500" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              {formError && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                  {formError}
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-600">Project description</label>
                <textarea
                  value={form.project_description}
                  onChange={setField("project_description")}
                  rows={2}
                  placeholder="e.g. 4-storey office block, Ikeja"
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Location</label>
                  <select value={form.state_id} onChange={setField("state_id")} className={inputCls}>
                    <option value="">—</option>
                    {states.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Client type</label>
                  <input
                    value={form.client_type}
                    onChange={setField("client_type")}
                    list="ec-client-types"
                    placeholder="e.g. Federal Government"
                    className={inputCls}
                  />
                  <datalist id="ec-client-types">
                    {clientTypes.map((t) => <option key={t.id} value={t.name} />)}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Project type</label>
                  <input
                    value={form.project_type}
                    onChange={setField("project_type")}
                    list="ec-project-types"
                    placeholder="e.g. Residential"
                    className={inputCls}
                  />
                  <datalist id="ec-project-types">
                    {projectTypes.map((t) => <option key={t.id} value={t.name} />)}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Project category</label>
                  <input
                    value={form.project_category}
                    onChange={setField("project_category")}
                    list="ec-project-categories"
                    placeholder="e.g. New build"
                    className={inputCls}
                  />
                  <datalist id="ec-project-categories">
                    {projectCategories.map((t) => <option key={t.id} value={t.name} />)}
                  </datalist>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600">
                  {editId ? "Replace file (optional)" : "Analysis file"}
                </label>
                <input
                  type="file"
                  accept={accepts.map((e) => `.${e}`).join(",")}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 mt-1"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  {accepts.join(", ")} · up to {Math.round((meta?.meta?.max_kilobytes ?? 30720) / 1024)} MB
                  {editId && " · leave empty to keep the current file"}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border text-sm">
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={uploading || updating}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-60"
              >
                {(uploading || updating) && <Loader2 className="w-4 h-4 animate-spin" />}
                {editId ? "Save changes" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ──────────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Delete this analysis?</h3>
            <p className="text-sm text-gray-500">
              {deleteTarget.project_description || deleteTarget.file_name}
            </p>
            <p className="text-xs text-rose-600">The stored file is deleted with it.</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 rounded-lg border text-sm">
                Cancel
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
